import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Box,
  Button,
  Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import usePushNotifications from '../hooks/usePushNotifications';

const PushNotificationToggle = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    error,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  if (!isSupported) {
    return (
      <Card sx={{
        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
        border: '1px solid',
        borderColor: 'rgba(212, 175, 55, 0.2)',
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NotificationsOffIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Push Notifications</Typography>
          </Box>
          <Alert severity="info">
            Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
      border: '1px solid',
      borderColor: 'rgba(212, 175, 55, 0.2)',
      boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
      },
    }}>
      <CardContent sx={{ pl: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isSubscribed ? (
              <NotificationsActiveIcon sx={{ mr: 1, color: '#D4AF37' }} />
            ) : (
              <NotificationsIcon sx={{ mr: 1, color: 'text.secondary' }} />
            )}
            <Typography variant="h6" fontWeight={600}>
              Push Notifications
            </Typography>
          </Box>
          <Chip
            label={isSubscribed ? 'Enabled' : 'Disabled'}
            color={isSubscribed ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Get instant notifications for class updates, schedule changes, fee reminders, and important announcements.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {permission === 'denied' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Notification permission has been blocked. To enable notifications:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 1 }}>
              <li>Click the lock icon in your browser's address bar</li>
              <li>Find "Notifications" in the permissions list</li>
              <li>Change the setting to "Allow"</li>
              <li>Reload the page</li>
            </Typography>
          </Alert>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={isSubscribed}
                onChange={handleToggle}
                color="primary"
                disabled={permission === 'denied'}
              />
            }
            label={
              <Typography variant="body2">
                {isSubscribed
                  ? 'You will receive push notifications'
                  : 'Turn on push notifications'}
              </Typography>
            }
          />
        )}

        {!isSubscribed && permission === 'default' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={subscribe}
              startIcon={<NotificationsActiveIcon />}
              sx={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #C5A028 0%, #E3C030 100%)',
                },
              }}
            >
              Enable Notifications
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationToggle;
